import { FC, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import {
  ButtonType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { epecialidadeVinculoSchema, VinculoMode } from './validation/epecialidadeVinculoSchema'
import {
  bindFormularioToEmpresasWithEspecialidade,
  bindFormularioToUnidadesWithEspecialidade,
} from '../../core/_requests'
import {
  Button,
  FormRadio,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { FormularioDtoEmpresaCreate, FormularioDtoUnidadeCreate } from '../../core/_models'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'

type IProps = {
  formularioId: number
}

export const EspecialidadeVinculoModal: FC<IProps> = ({ formularioId }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const initialValues: {
    especialidades: IOption<number>[]
    empresas: IOption<number>[]
    empresa: IOption<number> | undefined
    unidades: IOption<number>[]
    mode: VinculoMode | undefined
  } = {
    especialidades: [],
    empresas: [],
    empresa: undefined,
    unidades: [],
    mode: undefined,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => epecialidadeVinculoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      try {
        if (formik.values.mode === VinculoMode.Empresa) {
          const data: FormularioDtoEmpresaCreate = {
            formulario: Number(formularioId),
            especialidades: values.especialidades.map((especialidade) => especialidade.value),
            empresas: values.empresas.map((empresa) => empresa.value),
          }
          await bindFormularioToEmpresasWithEspecialidade(data)
          showAlert({ title: 'Sucesso', text: 'Vínculo criado com sucesso!', icon: 'success' })
        } else if (formik.values.mode === VinculoMode.Unidade) {
          const data: FormularioDtoUnidadeCreate = {
            formulario: Number(formularioId),
            especialidades: values.especialidades.map((especialidade) => especialidade.value),
            unidades: values.unidades.map((unidade) => unidade.value),
          }
          await bindFormularioToUnidadesWithEspecialidade(
            Number(formik.values.empresa?.value),
            data
          )
          showAlert({ title: 'Sucesso', text: 'Vínculo criado com sucesso!', icon: 'success' })
        }

        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik

  useEffect(() => {
    if (!formik.values.empresa) setFieldValue('unidades', [])
  }, [formik.values.empresa, setFieldValue])

  const empresaParams = useMemo(
    () => ({ empresaId: Number(formik.values.empresa?.value) }),
    [formik.values.empresa?.value]
  )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        label='Especialidades'
        name='especialidades'
        fetchOptions={OptionsHelper.getOptionsEspecialidades}
        placeholder='Selecione uma especialidade'
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
        isMulti
      />
      <FormRadio
        formik={formik}
        label='Vincular por'
        name='mode'
        options={[
          { id: 1, descricao: 'Instituição', value: VinculoMode.Empresa },
          { id: 2, descricao: 'Unidade', value: VinculoMode.Unidade },
        ]}
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
      />
      {formik.values.mode && (
        <>
          <FormSelect
            formik={formik}
            name={formik.values.mode === VinculoMode.Unidade ? 'empresa' : 'empresas'}
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            entity='empresa'
            isDisabled={formik.isSubmitting}
            isMulti={formik.values.mode !== VinculoMode.Unidade}
            className='mb-3'
            isRequired
          />
          {formik.values.mode === VinculoMode.Unidade && (
            <FormSelect
              formik={formik}
              name='unidades'
              label='Unidades/Setores'
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={empresaParams}
              placeholder='Selecione pelo menos uma unidade'
              isDisabled={formik.isSubmitting}
              className='mb-3'
              isRequired
              isMulti
            />
          )}
        </>
      )}

      <div className='d-flex justify-content-center pt-15'>
        <Button onClick={closeModal} className='btn-secondary me-3' disabled={formik.isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
